const THAI_BUDDHIST_CUTOFF = 2400;

export function normalizeDmaCode(value: unknown): string | null {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  if (/^\d{2}-\d{2}-\d{2}$/.test(raw)) return raw.replaceAll("-", "");
  if (/^\d{6}$/.test(raw)) return raw;
  return null;
}

export function parseNumber(value: unknown): number | null {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const normalized = raw.replaceAll(",", "");
  if (!/^-?(?:\d+\.?\d*|\.\d+)$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseThaiDate(value: unknown): string | null {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?Z?)?/);
  if (iso) {
    const [, yearText, monthText, dayText, hourText, minuteText, secondText] = iso;
    const date = `${yearText}-${monthText}-${dayText}`;
    return hourText ? `${date}T${hourText}:${minuteText ?? "00"}:${secondText ?? "00"}+07:00` : date;
  }
  const match = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (!match) return null;
  const [, dayText, monthText, yearText, hourText = "0", minuteText = "0", secondText = "0"] = match;
  const day = Number(dayText);
  const month = Number(monthText);
  let year = Number(yearText);
  if (year >= THAI_BUDDHIST_CUTOFF) year -= 543;
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const second = Number(secondText);
  if (month < 1 || month > 12 || day < 1 || hour > 23 || minute > 59 || second > 59) return null;
  const check = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null;
  const date = `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
  if (!match[4]) return date;
  return `${date}T${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}:${second.toString().padStart(2, "0")}+07:00`;
}

export function parseThaiTime(value: unknown): string | null {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const match = raw.match(/^(\d{1,2})[:.](\d{2})(?:[:.](\d{2}))?$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const second = Number(match[3] ?? 0);
  if (hour > 23 || minute > 59 || second > 59) return null;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}`;
}

export function combineThaiDateTime(dateValue: unknown, timeValue: unknown): { dateTime: string | null; time: string | null } {
  const date = parseThaiDate(dateValue);
  const time = parseThaiTime(timeValue);
  if (!date) return { dateTime: null, time };
  if (date.includes("T")) return { dateTime: date, time: date.slice(11, 19) };
  return { dateTime: time ? `${date}T${time}+07:00` : date, time };
}

export function resolveCoordinates(x: unknown, y: unknown): { latitude: number | null; longitude: number | null; swapped: boolean } {
  const first = parseNumber(x);
  const second = parseNumber(y);
  if (first === null || second === null || (first === 0 && second === 0)) return { latitude: null, longitude: null, swapped: false };
  const bangkok = (lat: number, lon: number) => lat >= 13 && lat <= 14.5 && lon >= 99.5 && lon <= 101.5;
  if (bangkok(first, second)) return { latitude: first, longitude: second, swapped: false };
  if (bangkok(second, first)) return { latitude: second, longitude: first, swapped: true };
  return { latitude: null, longitude: null, swapped: false };
}

export function safeCsvCell(value: unknown): string {
  const text = String(value ?? "");
  const protectedText = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return /[",\r\n]/.test(protectedText) ? `"${protectedText.replaceAll('"', '""')}"` : protectedText;
}

export function stableColumnIds(headers: string[]): { id: string; label: string }[] {
  const seen = new Map<string, number>();
  return headers.map((value, index) => {
    const label = value.trim() || `คอลัมน์ไม่มีชื่อ ${index + 1}`;
    const count = (seen.get(label) ?? 0) + 1;
    seen.set(label, count);
    return { id: `column_${index + 1}`, label: count === 1 ? label : `${label} (${count})` };
  });
}

